import {
  MANAGER_RESIDENTS_PAGE_SIZE,
  type ManagerResidentDetail,
  type ManagerResidentsFilter,
  type ManagerResidentsListData,
  type ManagerResidentRow,
  type ManagerResidentStatusOption,
  type ManagerResidentsClusterOption,
} from './types';

const MOCK_CLUSTERS = ['Образовательный', 'Технологический', 'Кластер Ломоносов', 'Междисциплинарный'];

const MOCK_STATUSES = ['Активен', 'На рассмотрении', 'Приостановлен'];

const MOCK_DESCRIPTION = `Наше дело не так однозначно, как может показаться: высокотехнологичная концепция общественного уклада требует анализа системы массового участия. С учётом сложившейся международной обстановки, граница обучения кадров однозначно определяет каждого участника как способного принимать собственные решения касаемо экспериментов, поражающих по своей масштабности и грандиозности.

Повседневная практика показывает, что социально-экономическое развитие предполагает независимые способы реализации позиций, занимаемых участниками в отношении поставленных задач.`;

const mockResidents: ManagerResidentRow[] = Array.from({ length: 50 }, (_, index) => {
  const id = 500 - index;

  return {
    id,
    company: 'ООО «Компания_имя»',
    cluster: MOCK_CLUSTERS[index % MOCK_CLUSTERS.length],
    manager: 'Иванов И. П.',
    phone: '+7 956 583-45-09',
    email: 'example@example.ru',
    status: MOCK_STATUSES[index % MOCK_STATUSES.length],
    editUrl: `/lk/manager/resident-add?id=${id}`,
  };
});

export const mockManagerResidentStatusOptions = MOCK_STATUSES.map((name, index) => ({
  value: String(index + 1),
  label: name,
}));

export function getMockManagerResidentDetail(
  id: number,
  clusters: ManagerResidentsClusterOption[] = [],
): ManagerResidentDetail {
  const row = mockResidents.find((item) => item.id === id);
  const clusterName = row?.cluster ?? 'Образовательный';
  const clusterId =
    clusters.find((cluster) => cluster.name === clusterName)?.id ??
    String(MOCK_CLUSTERS.indexOf(clusterName) + 1);

  return {
    id,
    status: {
      id: '1',
      name: row?.status ?? 'Активен',
    },
    statusOptions: mockManagerResidentStatusOptions.map((option) => ({
      id: option.value,
      name: option.label,
    })),
    logoUrl: '/assets/images/logo.svg',
    fullName: 'Boston Dynamics Incorporated',
    shortName: 'Boston Dynamics, Inc',
    projectDescription: MOCK_DESCRIPTION,
    inn: '09876543210',
    ogrn: '1027733380796',
    legalAddress: '119583, г. Санкт-Петербург, ул. Российская 128-а, ст. 16, офис 512',
    actualAddress: '120586, г. Москва, ул. Пушкина 128-а, ст. 16, офис 256',
    email: 'boston.d@gmail.com',
    phone: '8 495 505 02 04',
    website: 'site.example.com',
    clusterId,
    clusterName: `«${clusterName}»`,
    contact: {
      fullName: 'Грушевский Константин Павлович',
      position: 'Главный менеджер управления',
      phone: '8 495 505 02 04',
      email: 'grusha.kon@gmail.com',
    },
  };
}

export async function updateMockManagerResidentStatus(
  id: number,
  statusId: string,
): Promise<ManagerResidentStatusOption> {
  const status = mockManagerResidentStatusOptions.find((option) => option.value === statusId);
  const row = mockResidents.find((item) => item.id === id);

  if (row && status) {
    row.status = status.label;
  }

  return {
    id: statusId,
    name: status?.label ?? 'Активен',
  };
}

export async function updateMockManagerResidentCluster(
  id: number,
  clusterId: string,
  clusters: ManagerResidentsClusterOption[],
): Promise<{ clusterId: string; clusterName: string }> {
  const cluster = clusters.find((item) => item.id === clusterId);
  const clusterName = cluster?.name ?? MOCK_CLUSTERS[0];

  const row = mockResidents.find((item) => item.id === id);
  if (row) {
    row.cluster = clusterName;
  }

  return {
    clusterId,
    clusterName: `«${clusterName}»`,
  };
}

function filterMockResidents(
  items: ManagerResidentRow[],
  q?: string,
  filter?: ManagerResidentsFilter,
): ManagerResidentRow[] {
  let result = [...items];

  const query = q?.trim().toLowerCase();
  if (query && query.length >= 3) {
    result = result.filter((item) => {
      const haystack = [item.company, item.cluster, item.manager, item.phone, item.email, String(item.id)]
        .join(' ')
        .toLowerCase();

      return haystack.includes(query);
    });
  }

  if (filter?.clusterId) {
    const clusterName = MOCK_CLUSTERS.find((_, index) => String(index + 1) === filter.clusterId);
    if (clusterName) {
      result = result.filter((item) => item.cluster === clusterName);
    }
  }

  if (filter?.statusId) {
    const statusName = MOCK_STATUSES.find((_, index) => String(index + 1) === filter.statusId);
    if (statusName) {
      result = result.filter((item) => item.status === statusName);
    }
  }

  return result;
}

export function getMockManagerResidentsList(params: {
  q?: string;
  page?: number;
  filter?: ManagerResidentsFilter;
}): ManagerResidentsListData {
  const page = params.page && params.page > 0 ? params.page : 1;
  const filtered = filterMockResidents(mockResidents, params.q, params.filter);
  const start = (page - 1) * MANAGER_RESIDENTS_PAGE_SIZE;
  const items = filtered.slice(start, start + MANAGER_RESIDENTS_PAGE_SIZE);

  return {
    items,
    nav: {
      totalCount: filtered.length,
      pageSize: MANAGER_RESIDENTS_PAGE_SIZE,
      currentPage: page,
      pageCount: Math.max(1, Math.ceil(filtered.length / MANAGER_RESIDENTS_PAGE_SIZE)),
    },
    clusters: MOCK_CLUSTERS.map((name, index) => ({
      id: String(index + 1),
      name,
    })),
  };
}
